import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { User } from '../../models/User'
import { ContactChange } from '../../models/MeetingSupport'

export enum CONTACT_UPDATE_STEP {
  CONTACT_SELECT_STEP = 'contactSelectStep',
  CONTACT_UPDATE_STEP = 'contactUpdateStep',
}

interface NewContactData {
  firstName?: string
  lastName?: string
  name?: string
  email?: string
}

interface ContactUpdateState {
  createNewCRMContact: boolean
  newContactData: NewContactData | null
  externalId: string | null
  selectedZocksContact: User | null
  contact: User | null
  isLoadingContact: boolean
  contactChange: ContactChange | null
  isLoadingContactChange: boolean
  existingExternalContact: boolean
  showCreateContact: boolean
  shouldCreateContact: boolean
  showLinkContact: boolean
  shouldLinkContact: boolean
  isLoadingSave: boolean
  isLoadingRerun: boolean
  selectedKeys: string[]
  step: CONTACT_UPDATE_STEP
  isSearchingForEmail: boolean
  isInitializing: boolean
  shouldReload: boolean
  updateStartedFrom: string | null
}

const initialState: ContactUpdateState = {
  createNewCRMContact: true,
  newContactData: null,
  externalId: null,
  selectedZocksContact: null,
  contact: null,
  isLoadingContact: true,
  contactChange: null,
  isLoadingContactChange: true,
  existingExternalContact: true,
  showCreateContact: false,
  shouldCreateContact: false,
  showLinkContact: false,
  shouldLinkContact: false,
  isLoadingSave: false,
  isLoadingRerun: false,
  selectedKeys: [],
  step: CONTACT_UPDATE_STEP.CONTACT_SELECT_STEP,
  isSearchingForEmail: false,
  isInitializing: true,
  shouldReload: false,
  updateStartedFrom: null,
}

export const contactUpdateSlice = createSlice({
  name: 'contactUpdate',
  initialState,
  reducers: {
    setIsLoadingContact: (state, action: PayloadAction<boolean>) => {
      state.isLoadingContact = action.payload
    },
    setContact: (state, action: PayloadAction<User | null>) => {
      state.contact = action.payload
    },
    setSelectedZocksContact: (state, action: PayloadAction<User | null>) => {
      state.selectedZocksContact = action.payload
    },
    setContactChange: (state, action: PayloadAction<ContactChange | null>) => {
      state.contactChange = action.payload
    },
    setIsLoadingContactChange: (state, action: PayloadAction<boolean>) => {
      state.isLoadingContactChange = action.payload
    },
    setExistingExternalContact: (state, action: PayloadAction<boolean>) => {
      state.existingExternalContact = action.payload
    },
    setShowCreateContact: (state, action: PayloadAction<boolean>) => {
      state.showCreateContact = action.payload
    },
    setShouldCreateContact: (state, action: PayloadAction<boolean>) => {
      state.shouldCreateContact = action.payload
    },
    setShowLinkContact: (state, action: PayloadAction<boolean>) => {
      state.showLinkContact = action.payload
    },
    setShouldLinkContact: (state, action: PayloadAction<boolean>) => {
      state.shouldLinkContact = action.payload
    },
    setIsLoadingSave: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSave = action.payload
    },
    setIsLoadingRerun: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRerun = action.payload
    },
    setSelectedKeys: (state, action: PayloadAction<string[]>) => {
      state.selectedKeys = action.payload
    },
    deselectKeys: (state, action: PayloadAction<string[]>) => {
      state.selectedKeys = state.selectedKeys.filter((key) => !action.payload.includes(key))
    },
    setExternalId: (state, action: PayloadAction<string | null>) => {
      state.externalId = action.payload
    },
    setContactUpdateStep: (state, action: PayloadAction<CONTACT_UPDATE_STEP>) => {
      return { ...state, step: action.payload }
    },
    setCreateNewCRMContact: (state, action: PayloadAction<boolean>) => {
      return { ...state, createNewCRMContact: action.payload }
    },
    setNewContactData: (state, action: PayloadAction<NewContactData>) => {
      return { ...state, newContactData: action.payload }
    },
    setIsSearchingForEmail: (state, action: PayloadAction<boolean>) => {
      return { ...state, isSearchingForEmail: action.payload }
    },
    setIsInitializing: (state, action: PayloadAction<boolean>) => {
      return { ...state, isInitializing: action.payload }
    },
    setShouldReload: (state, action: PayloadAction<boolean>) => {
      return { ...state, shouldReload: action.payload }
    },
    setUpdateStartedFrom: (state, action: PayloadAction<string | null>) => {
      return { ...state, updateStartedFrom: action.payload }
    },
    resetContactUpdate: () => initialState,
  },
})

export const {
  setIsLoadingContact,
  setSelectedZocksContact,
  setContact,
  setExistingExternalContact,
  setShowCreateContact,
  setShowLinkContact,
  setShouldLinkContact,
  setShouldCreateContact,
  setContactChange,
  setIsLoadingContactChange,
  setIsLoadingSave,
  setIsLoadingRerun,
  setSelectedKeys,
  deselectKeys,
  setExternalId,
  resetContactUpdate,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setNewContactData,
  setIsSearchingForEmail,
  setIsInitializing,
  setShouldReload,
  setUpdateStartedFrom,
} = contactUpdateSlice.actions

export const selectContact = (state: RootState) => state.meetingSummary.contactUpdate.contact

export const selectSelectedZocksContact = (state: RootState) => state.meetingSummary.contactUpdate.selectedZocksContact

export const selectIsLoadingContact = (state: RootState) => state.meetingSummary.contactUpdate.isLoadingContact

export const selectContactChange = (state: RootState) => state.meetingSummary.contactUpdate.contactChange

export const selectIsLoadingContactChange = (state: RootState) => state.meetingSummary.contactUpdate.isLoadingContactChange

export const selectIsExistingExternalContact = (state: RootState) => state.meetingSummary.contactUpdate.existingExternalContact

export const selectShowCreateContact = (state: RootState) => state.meetingSummary.contactUpdate.showCreateContact

export const selectShouldCreateContact = (state: RootState) => state.meetingSummary.contactUpdate.shouldCreateContact

export const selectShowLinkContact = (state: RootState) => state.meetingSummary.contactUpdate.showLinkContact

export const selectShouldLinkContact = (state: RootState) => state.meetingSummary.contactUpdate.shouldLinkContact

export const selectIsLoadingSave = (state: RootState) => state.meetingSummary.contactUpdate.isLoadingSave

export const selectIsLoadingRerun = (state: RootState) => state.meetingSummary.contactUpdate.isLoadingRerun

export const selectSelectedKeys = (state: RootState) => state.meetingSummary.contactUpdate.selectedKeys

export const selectExternalId = (state: RootState) => state.meetingSummary.contactUpdate.externalId

export const selectContactUpdateStep = (state: RootState) => state.meetingSummary.contactUpdate.step

export const selectCreateNewCRMContact = (state: RootState) => state.meetingSummary.contactUpdate.createNewCRMContact

export const selectNewContactData = (state: RootState) => state.meetingSummary.contactUpdate.newContactData

export const selectIsSearchingForEmail = (state: RootState) => state.meetingSummary.contactUpdate.isSearchingForEmail

export const selectIsInitializing = (state: RootState) => state.meetingSummary.contactUpdate.isInitializing

export const selectShouldReload = (state: RootState) => state.meetingSummary.contactUpdate.shouldReload

export const selectUpdateStartedFrom = (state: RootState) => state.meetingSummary.contactUpdate.updateStartedFrom
